import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { NextPage, GetServerSideProps } from 'next';

import posthog from 'posthog-js';
import Head from '../components/Head';
import { H5, S1, Caption, H3Styles, H5Styles } from '../components/Typography';
import { ShopPageWrapper } from './shop/[collection]';

import { getProductsByQuery } from '../api/product';
import {
  getCollectionByHandle,
  getCreatorCollectionProductsByQuery,
  getCreatorCollectionsByQuery,
  getSearchableCollectionsByQuery,
} from '../api/collections';
import { findCollectionByMetafield, getShopifyQuery } from '../utils/product';
import {
  filterProducts,
  getSortedCreatorSearchResults,
  getSortedProducts,
  getSortedProductSearchResults,
} from '../utils/collections';

import config from '../../config.json';
import { getPropsForPageLoad } from '../utils/pageLoad';
import CreatorsDisplay from '../components/CreatorsDisplay';
import CreatorProducts from '../components/CreatorProducts';
import { CampaignStatus } from '../types/common';

const { pagination, collections } = config;

const SearchQuery = styled(H5)`
  margin-bottom: 64px;
  @media screen and (min-width: ${({ theme }) => theme.breakpoints.desktop}px) {
    ${H3Styles}
  }
`;

const UnsuccessfulWrapper = styled.div`
  margin: 0 auto;
  text-align: center;
  margin-bottom: 48px;
  @media screen and (max-width: ${({ theme }) => theme.breakpoints.tablet}px) {
    text-align: left;
  }
`;

const UnsuccessfulH5 = styled(H5)`
  margin-bottom: 8px;
`;

const ResultsHeader = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin: 0 10px 12px 10px;
  @media screen and (min-width: ${({ theme }) => theme.breakpoints.tablet}px) {
    margin: 12px 12px;
  }
  @media screen and (min-width: ${({ theme }) => theme.breakpoints.desktop}px) {
    margin: 12px 24px;
  }
`;

const ResultCount = styled(S1)`
  padding-top: 26px;
  @media screen and (min-width: ${({ theme }) => theme.breakpoints.desktop}px) {
    ${H5Styles}
  }
`;

const SearchPageContents = styled.div`
  margin-top: 16px;
`;

const CreatorsDisplayWrapper = styled.div`
  margin: 0 10px 12px 10px;
  @media screen and (min-width: ${({ theme }) => theme.breakpoints.tablet}px) {
    margin: 12px 12px;
  }
  @media screen and (min-width: ${({ theme }) => theme.breakpoints.desktop}px) {
    margin: 12px 24px;
  }
`;

const CreatorProductsWrapper = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
`;

const CreatorProductsDisplayWrapper = styled.div`
  margin-bottom: 20px;

  @media screen and (min-width: ${({ theme }) => theme.breakpoints.desktop}px) {
    margin-bottom: 20px;
  }
`;

type SearchResultsType = {
  productResults: Shopify.ProductEdge[];
  creatorResults: Shopify.CollectionEdge[];
};

type SearchPageProps = {
  query: string;
  results: SearchResultsType;
  successful: boolean;
};

const SearchPage: NextPage<SearchPageProps> = ({ query, results, successful }: SearchPageProps) => {
  const [pageProducts, setPageProducts] = useState<Shopify.ProductEdge[]>([]);
  const [pageCreators, setPageCreators] = useState<Shopify.CollectionEdge[]>([]);
  const campaignStatuses = [
    { tag: 'live', status: CampaignStatus.Live },
    { tag: 'comingsoon', status: CampaignStatus.ComingSoon },
    { tag: 'past', status: CampaignStatus.Past },
  ];
  const filteredProducts = (tag: string) =>
    getSortedProducts(filterProducts(pageProducts.filter((product) => product.node.tags.includes(tag))));

  useEffect(() => {
    setPageProducts(results.productResults);
    setPageCreators(getSortedCreatorSearchResults(results.creatorResults));
    posthog.capture('view_search_results', { query, resultsFound: successful });
  }, [results]);

  return (
    <>
      <Head title={query} url="/search" />
      <ShopPageWrapper>
        {successful ? (
          <ResultsHeader>
            <div>
              <Caption>Search results for</Caption>
              <SearchQuery>&ldquo;{query}&rdquo;</SearchQuery>
            </div>
            <ResultCount>{results.productResults.length + results.creatorResults.length} results</ResultCount>
          </ResultsHeader>
        ) : (
          <UnsuccessfulWrapper>
            <UnsuccessfulH5>No results found for &ldquo;{query}&rdquo;</UnsuccessfulH5>
            <S1>You may like these newest drops:</S1>
          </UnsuccessfulWrapper>
        )}
        <SearchPageContents>
          <CreatorsDisplayWrapper>
            <CreatorsDisplay creators={pageCreators} titleForTracking="search" query={query} />
          </CreatorsDisplayWrapper>
          <CreatorProductsWrapper>
            {campaignStatuses.map(({ tag, status }) => {
              if (filteredProducts(tag).length === 0) {
                return <></>;
              }
              return (
                <>
                  <CreatorProductsDisplayWrapper key={status}>
                    <CreatorProducts
                      key={status}
                      products={filteredProducts(tag)}
                      campaignStatus={status}
                      isCreatorBrandedPage={false}
                      isPreview={false}
                    />
                  </CreatorProductsDisplayWrapper>
                </>
              );
            })}
          </CreatorProductsWrapper>
        </SearchPageContents>
      </ShopPageWrapper>
    </>
  );
};

export const getServerSideProps: GetServerSideProps<SearchPageProps> = async (ctx) => {
  const { query } = ctx;
  if (query.q) {
    let successful = true;

    const [creatorCollections, productsByTitle, productsByCreator, searchableProducts] = await Promise.all([
      getCreatorCollectionsByQuery(getShopifyQuery(query.q as string), 25, '', 'UPDATED_AT', true, 1),
      getProductsByQuery(getShopifyQuery(query.q as string), 25, '', 'UPDATED_AT', true, 1),
      getCreatorCollectionProductsByQuery(getShopifyQuery(query.q as string), 25, '', 'UPDATED_AT', true, 1),
      getSearchableCollectionsByQuery(getShopifyQuery(query.q as string), 25, '', 'UPDATED_AT', true, 1),
    ]);

    const productResults = getSortedProductSearchResults(productsByTitle, productsByCreator, searchableProducts);

    const creatorResults = getSortedCreatorSearchResults(creatorCollections);

    const results = {
      productResults,
      creatorResults,
    };

    if (productResults.length === 0 && creatorResults.length === 0) {
      successful = false;
      results.productResults = await getCollectionByHandle(
        collections.topCampaigns,
        undefined,
        1,
        pagination.carousel,
      ).then((data) => data?.products.edges as Shopify.ProductEdge[]);

      const creatorCollection =
        results.productResults
          .slice(0, 3)
          .map((product) => {
            const collection = findCollectionByMetafield(product.node, 'isCreatorCollection', 'true');
            return collection || null;
          })
          .filter((collection) => collection !== null) || [];

      results.creatorResults = creatorCollection as Shopify.CollectionEdge[];
    }

    const propsForPageLoad = await getPropsForPageLoad(ctx);

    return {
      props: {
        results,
        query: query.q as string,
        successful,
        ...propsForPageLoad,
      },
    };
  }
  return {
    notFound: true,
  };
};

export default SearchPage;
